import { ChangeEvent, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Column } from "../../../../core/components/Column";
import { Container } from "../../../../core/components/Container";
import { ButtonBack, CustomButton } from "../../../../core/components/CustomButton";
import { CustomInput } from "../../../../core/components/CustomInput";
import { LogoTitleBlack } from "../../../../core/components/LogoTitle";
import { TextComponent } from "../../../../core/components/TextComponent";
import { AppDispatch } from "../../../../core/store/store";
import { onLoginUser } from "../store/auth/AuthThunk";
import { buildAuthQuery, getSafeNextPath } from "../routes/affiliateRedirect";
import { getPostLoginPath } from "../../../../core/utilities/accountRecovery";

const emailPattern = /^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/;

export const PasswordLoginScreen = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const dispatch: AppDispatch = useDispatch();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const authQuery = buildAuthQuery(
    searchParams.get("intent"),
    searchParams.get("next")
  );
  const nextPath = getSafeNextPath(searchParams.get("next"));

  const onBack = () => {
    navigate(-1);
  };

  const onSubmit = async () => {
    const trimmedEmail = email.trim().toLowerCase();
    if (!emailPattern.test(trimmedEmail)) {
      setErrorMessage("Please enter a valid email address.");
      return;
    }
    if (password.length < 8) {
      setErrorMessage("Password must be at least 8 characters.");
      return;
    }

    setErrorMessage("");
    const resp = await dispatch(
      onLoginUser({
        emailOrPhone: trimmedEmail,
        type: "0",
        otp: "",
        isLogin: true,
        password,
      })
    );

    if (resp.meta.requestStatus === "fulfilled") {
      navigate(getPostLoginPath((resp as any).payload, nextPath));
      return;
    }

    const message = (resp as any).error?.message;
    if (message === "PASSWORD_NOT_SET") {
      setErrorMessage(
        "No password is set for this account yet. Use email code first, then create one."
      );
      return;
    }
    if (message === "INVALID_AUTH") {
      setErrorMessage(
        "Invalid email or password. If needed, reset it with email code."
      );
      return;
    }
    if (message === "403") {
      setErrorMessage("This account has been banned. Please contact support.");
      return;
    }
    setErrorMessage("Something went wrong. Please try again later.");
  };

  return (
    <Container className="flex flex-col w-full md:w-1/2 md:justify-center">
      <LogoTitleBlack />
      <Column crossAxisAlignment="center" className="max-md:bg-black">
        <Column
          mainAxisAlignment="center"
          className="w-full bg-white rounded-tl-3xl max-md:py-14 max-md:px-8 md:mx-24 md:w-1/2"
        >
          <ButtonBack
            onClick={onBack}
            className="relative pb-6 md:p-0 md:absolute md:top-20"
          >
            Go Back
          </ButtonBack>
          <TextComponent variant="h1" className="font-bold">
            Login with password
          </TextComponent>
          <TextComponent variant="h6" className="text-gray-500 mt-3">
            Use this only if you already created a password before.
          </TextComponent>
          <button
            type="button"
            className="mt-3 text-left text-primary underline"
            onClick={() => navigate(`/auth/email?mode=login${authQuery ? `&${authQuery.slice(1)}` : ""}`)}
          >
            No password yet? Use email code and create one.
          </button>
          <button
            type="button"
            className="mt-2 text-left text-primary underline"
            onClick={() => navigate(`/auth/email?mode=reset${authQuery ? `&${authQuery.slice(1)}` : ""}`)}
          >
            Forgot password? Reset with email code
          </button>
          <div className="mt-5">
            <CustomInput
              label="Email Address"
              hintText="Enter email address"
              value={email}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                setEmail(e.target.value)
              }
              type="email"
            />
          </div>
          <div className="mt-4">
            <CustomInput
              label="Password"
              hintText="Enter password"
              value={password}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                setPassword(e.target.value)
              }
              obscureText
            />
          </div>
          {errorMessage && <div className="mt-3 text-red-500">{errorMessage}</div>}
          <CustomButton type="button" className="mt-9" onClick={onSubmit}>
            LOGIN
          </CustomButton>
        </Column>
      </Column>
    </Container>
  );
};
